import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaCheck, FaTimes, FaEye, FaDownload, FaSearch, FaSort, FaSortUp, FaSortDown, FaChartLine } from 'react-icons/fa';
import { getVerifiedLeads, updateLeadStatus } from '../services/emailVerifier';

const VerifiedLeadsTable = ({ sessionId, onLeadsSelected }) => {
  const [verifiedLeads, setVerifiedLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('email');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [leadsPerPage] = useState(50);
  const [lastClickedLeadId, setLastClickedLeadId] = useState(null);

  useEffect(() => {
    if (sessionId) {
      // Reset state when session changes
      setSelectedLeads([]);
      setSearchTerm('');
      setCurrentPage(1);
      setSortField('email');
      setSortDirection('asc');
      setLastClickedLeadId(null);
      loadVerifiedLeads();
    } else {
      // Clear data when no session is selected
      setVerifiedLeads([]);
      setSelectedLeads([]);
      setError(null);
      setLoading(false);
      setLastClickedLeadId(null);
    }
  }, [sessionId]);

  // Reset lastClickedLeadId when page changes to prevent cross-page range selection
  useEffect(() => {
    setLastClickedLeadId(null);
  }, [currentPage, searchTerm, sortField, sortDirection]);

  const loadVerifiedLeads = async () => {
    try {
      setLoading(true);
      const leads = await getVerifiedLeads(sessionId);
      setVerifiedLeads(leads);
      setError(null);
    } catch (err) {
      console.error('Error loading verified leads:', err);
      setError('Failed to load verified leads');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort leads
  const filteredLeads = verifiedLeads.filter(lead => {
    const searchLower = searchTerm.toLowerCase();
    return (
      lead.email?.toLowerCase().includes(searchLower) ||
      lead.company?.toLowerCase().includes(searchLower) ||
      lead.first_name?.toLowerCase().includes(searchLower) ||
      lead.last_name?.toLowerCase().includes(searchLower) ||
      lead.job_title?.toLowerCase().includes(searchLower) ||
      lead.url?.toLowerCase().includes(searchLower)
    );
  });

  const sortedLeads = [...filteredLeads].sort((a, b) => {
    const aValue = a[sortField] || '';
    const bValue = b[sortField] || '';
    const comparison = aValue.toString().localeCompare(bValue.toString());
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  // Pagination
  const totalPages = Math.ceil(sortedLeads.length / leadsPerPage);
  const startIndex = (currentPage - 1) * leadsPerPage;
  const paginatedLeads = sortedLeads.slice(startIndex, startIndex + leadsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <FaSort />;
    return sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />;
  };

  const handleLeadSelection = (leadId, isSelected, event = null) => {
    // Handle shift-click range selection
    if (event && event.shiftKey && lastClickedLeadId !== null) {
      // Find indices in the paginated leads (what's currently visible)
      const lastClickedIndex = paginatedLeads.findIndex(lead => lead.id === lastClickedLeadId);
      const currentClickedIndex = paginatedLeads.findIndex(lead => lead.id === leadId);
      
      if (lastClickedIndex !== -1 && currentClickedIndex !== -1) {
        const startIndex = Math.min(lastClickedIndex, currentClickedIndex);
        const endIndex = Math.max(lastClickedIndex, currentClickedIndex);
        
        // Get all leads in the range
        const leadsInRange = paginatedLeads.slice(startIndex, endIndex + 1);
        const rangeIds = leadsInRange.map(lead => lead.id);
        
        // Select all leads in the range
        setSelectedLeads(prev => {
          // Remove any existing selections from the range first
          const withoutRange = prev.filter(id => !rangeIds.includes(id));
          // Then add all leads in the range
          return [...withoutRange, ...rangeIds];
        });
      }
    } else {
      // Normal single selection
      setSelectedLeads(prev => {
        if (isSelected) {
          return [...prev, leadId];
        } else {
          return prev.filter(id => id !== leadId);
        }
      });
    }
    
    // Update last clicked lead ID for future shift-clicks (only for non-shift clicks)
    if (!event || !event.shiftKey) {
      setLastClickedLeadId(leadId);
    }
  };

  const handleSelectAll = () => {
    const currentPageLeadIds = paginatedLeads.map(lead => lead.id);
    const allCurrentSelected = currentPageLeadIds.every(id => selectedLeads.includes(id));
    
    if (allCurrentSelected) {
      // Deselect all on current page
      setSelectedLeads(prev => prev.filter(id => !currentPageLeadIds.includes(id)));
    } else {
      // Select all on current page
      setSelectedLeads(prev => {
        const newSelected = [...prev];
        currentPageLeadIds.forEach(id => {
          if (!newSelected.includes(id)) {
            newSelected.push(id);
          }
        });
        return newSelected;
      });
    }
  };

  const handleSelectAllFiltered = () => {
    const allFilteredIds = sortedLeads.map(lead => lead.id);
    const allFilteredSelected = allFilteredIds.every(id => selectedLeads.includes(id));
    
    if (allFilteredSelected) {
      setSelectedLeads(prev => prev.filter(id => !allFilteredIds.includes(id)));
    } else {
      setSelectedLeads(prev => {
        const newSelected = [...prev];
        allFilteredIds.forEach(id => {
          if (!newSelected.includes(id)) {
            newSelected.push(id);
          }
        });
        return newSelected;
      });
    }
  };

  const handleAnalyzeSelected = async () => {
    if (selectedLeads.length === 0) {
      setError('Please select at least one lead to analyze');
      return;
    }

    const selectedLeadData = verifiedLeads.filter(lead => selectedLeads.includes(lead.id));
    
    // Format data for website analysis
    const formattedLeads = selectedLeadData.map(lead => ({
      email: lead.email,
      company: lead.company || '',
      firstName: lead.first_name || '',
      lastName: lead.last_name || '',
      jobTitle: lead.job_title || '',
      url: lead.url || '', // Changed from 'website' to 'url' to match websiteAnalyzer expectations
      phoneNumber: lead.phone_number || '',
      country: lead.country || '',
      industry: lead.industry || '',
      companyLinkedIn: lead.company_linkedin || '',
      linkedinUrl: lead.linkedin_url || lead.person_linkedin || '', // Use linkedin_url from database
      linkedinUsername: lead.linkedin_username || '', // Use linkedin_username from database
      source: 'verified_leads',
      leadId: lead.id
    })).filter(lead => lead.url); // Only include leads with URLs

    if (formattedLeads.length === 0) {
      setError('Selected leads must have website URLs for analysis');
      return;
    }

    try {
      // Update status of selected leads to "analyzed"
      const leadIdsToUpdate = formattedLeads.map(lead => lead.leadId);
      await updateLeadStatus(leadIdsToUpdate, 'analyzed');
      
      // Refresh the leads data to show updated status
      await loadVerifiedLeads();
      
      // Clear selection
      setSelectedLeads([]);
    } catch (error) {
      console.error('Error updating lead status:', error);
      setError('Failed to update lead status');
    }

    if (onLeadsSelected) {
      onLeadsSelected(formattedLeads);
    }

    // Navigate to dashboard
    window.dispatchEvent(new CustomEvent('navigate', { detail: { tab: 'dashboard' } }));
  };

  const exportSelectedLeads = () => {
    const selectedLeadData = verifiedLeads.filter(lead => selectedLeads.includes(lead.id));
    
    const csvContent = [
      ['Email', 'Company', 'First Name', 'Last Name', 'Job Title', 'Website', 'Phone Number', 'Country', 'Industry', 'Company LinkedIn', 'LinkedIn URL', 'LinkedIn Username', 'Verification Status'],
      ...selectedLeadData.map(lead => [
        lead.email,
        lead.company || '',
        lead.first_name || '',
        lead.last_name || '',
        lead.job_title || '',
        lead.url || '',
        lead.phone_number || '',
        lead.country || '',
        lead.industry || '',
        lead.company_linkedin || '',
        lead.linkedin_url || '',
        lead.linkedin_username || '',
        'Verified Safe'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `verified_leads_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading verified leads...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <FaTimes className="error-icon" />
        <p>{error}</p>
        <button onClick={loadVerifiedLeads} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="verified-leads-table"
    >
      <div className="table-header-section">
        <div className="header-info">
          <h2>Verified Leads</h2>
          <p>{verifiedLeads.length} verified leads • {selectedLeads.length} selected</p>
        </div>
        
        <div className="table-controls">
          <div className="search-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search leads..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="search-input"
            />
          </div>
          
          <div className="action-buttons">
            <button 
              onClick={handleSelectAllFiltered}
              className="select-button"
              disabled={filteredLeads.length === 0}
            >
              {sortedLeads.every(lead => selectedLeads.includes(lead.id)) ? 'Deselect All' : 'Select All Filtered'}
            </button>
            
            <button 
              onClick={exportSelectedLeads}
              disabled={selectedLeads.length === 0}
              className="export-button"
            >
              <FaDownload /> Export ({selectedLeads.length})
            </button>
            
            <button 
              onClick={handleAnalyzeSelected}
              disabled={selectedLeads.length === 0}
              className="analyze-button"
            >
              <FaEye /> Analyze Selected ({selectedLeads.length})
            </button>
          </div>
        </div>
      </div>

      {filteredLeads.length === 0 ? (
        <div className="no-results">
          <p>No leads found matching your search criteria.</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="leads-table">
              <thead>
                <tr>
                  <th className="checkbox-column">
                    <input
                      type="checkbox"
                      checked={paginatedLeads.length > 0 && paginatedLeads.every(lead => selectedLeads.includes(lead.id))}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th onClick={() => handleSort('email')} className="sortable">
                    <div className="sort-content">
                      Email {getSortIcon('email')}
                    </div>
                  </th>
                  <th onClick={() => handleSort('company')} className="sortable">
                    <div className="sort-content">
                      Company {getSortIcon('company')}
                    </div>
                  </th>
                  <th onClick={() => handleSort('first_name')} className="sortable">
                    <div className="sort-content">
                      First Name {getSortIcon('first_name')}
                    </div>
                  </th>
                  <th onClick={() => handleSort('last_name')} className="sortable">
                    <div className="sort-content">
                      Last Name {getSortIcon('last_name')}
                    </div>
                  </th>
                  <th onClick={() => handleSort('job_title')} className="sortable">
                    <div className="sort-content">
                      Job Title {getSortIcon('job_title')}
                    </div>
                  </th>
                  <th onClick={() => handleSort('url')} className="sortable">
                    <div className="sort-content">
                      Website {getSortIcon('url')}
                    </div>
                  </th>
                  <th onClick={() => handleSort('country')} className="sortable">
                    <div className="sort-content">
                      Country {getSortIcon('country')}
                    </div>
                  </th>
                  <th onClick={() => handleSort('industry')} className="sortable">
                    <div className="sort-content">
                      Industry {getSortIcon('industry')}
                    </div>
                  </th>
                  <th>Verification Status</th>
                  <th onClick={() => handleSort('status')} className="sortable">
                    <div className="sort-content">
                      Analysis Status {getSortIcon('status')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedLeads.map((lead, index) => (
                  <tr key={lead.id} className={`${selectedLeads.includes(lead.id) ? 'selected' : ''} ${lead.status === 'analyzed' ? 'analyzed-row' : ''}`}>
                    <td className="checkbox-column">
                      <input
                        type="checkbox"
                        checked={selectedLeads.includes(lead.id)}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleLeadSelection(lead.id, !selectedLeads.includes(lead.id), e);
                        }}
                        readOnly
                      />
                    </td>
                    <td className="email-cell">{lead.email}</td>
                    <td>{lead.company || '-'}</td>
                    <td>{lead.first_name || '-'}</td>
                    <td>{lead.last_name || '-'}</td>
                    <td>{lead.job_title || '-'}</td>
                    <td className="website-cell">
                      {lead.url ? (
                        <a href={lead.url} target="_blank" rel="noopener noreferrer">
                          {lead.url}
                        </a>
                      ) : '-'}
                    </td>
                    <td>{lead.country || '-'}</td>
                    <td>{lead.industry || '-'}</td>
                    <td>
                      <span className="status-badge verified">
                        <FaCheck /> Verified Safe
                      </span>
                    </td>
                    <td>
                      {lead.status === 'analyzed' ? (
                        <span className="status-badge analyzed">
                          <FaChartLine /> Analyzed
                        </span>
                      ) : (
                        <span className="status-badge pending">
                          Pending Analysis
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="pagination-button"
              >
                Previous
              </button>
              
              <span className="pagination-info">
                Page {currentPage} of {totalPages} ({sortedLeads.length} leads)
              </span>
              
              <button 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="pagination-button"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      <style jsx="true">{`
        .verified-leads-table {
          padding: 2rem;
          max-width: 100%;
          margin: 0 auto;
        }

        .table-header-section {
          margin-bottom: 2rem;
        }

        .header-info h2 {
          margin: 0 0 0.5rem 0;
          color: #1f2937;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .header-info p {
          margin: 0;
          color: #6b7280;
          font-size: 0.95rem;
        }

        .table-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
          margin-top: 1rem;
          flex-wrap: wrap;
        }

        .search-container {
          position: relative;
          flex: 1;
          max-width: 300px;
        }

        .search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
          font-size: 0.9rem;
        }

        .search-input {
          width: 100%;
          padding: 10px 12px 10px 36px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 0.95rem;
          transition: border-color 0.2s;
        }

        .search-input:focus {
          outline: none;
          border-color: #3b82f6;
        }

        .action-buttons {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .select-button, .export-button, .analyze-button {
          padding: 10px 16px;
          border: none;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s;
          font-size: 0.9rem;
        }

        .select-button {
          background: #f3f4f6;
          color: #374151;
          border: 1px solid #d1d5db;
        }

        .select-button:hover:not(:disabled) {
          background: #e5e7eb;
        }

        .export-button {
          background: #10b981;
          color: white;
        }

        .export-button:hover:not(:disabled) {
          background: #059669;
        }

        .analyze-button {
          background: #3b82f6;
          color: white;
        }

        .analyze-button:hover:not(:disabled) {
          background: #2563eb;
        }

        .select-button:disabled, .export-button:disabled, .analyze-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .table-container {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          overflow-x: auto;
          overflow-y: hidden;
          border: 1px solid #e5e7eb;
        }

        .leads-table {
          width: 100%;
          min-width: 1200px;
          border-collapse: collapse;
          font-size: 0.9rem;
        }

        .leads-table th {
          background: #f8fafc;
          padding: 12px 16px;
          text-align: left;
          font-weight: 600;
          color: #374151;
          border-bottom: 2px solid #e5e7eb;
          white-space: nowrap;
        }

        .leads-table th.sortable {
          cursor: pointer;
          user-select: none;
          transition: background-color 0.2s;
          position: relative;
        }

        .leads-table th.sortable:hover {
          background: #f1f5f9;
        }

        .leads-table th.sortable .sort-content {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .leads-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #f3f4f6;
          vertical-align: top;
        }

        .leads-table tr:hover {
          background: #f8fafc;
        }

        .leads-table tr.selected {
          background: #eff6ff;
        }

        .leads-table tr.selected:hover {
          background: #dbeafe;
        }

        .leads-table tr.analyzed-row {
          background: #f0fdf4;
        }

        .leads-table tr.analyzed-row:hover {
          background: #dcfce7;
        }

        .leads-table tr.analyzed-row.selected {
          background: #bbf7d0;
        }

        .leads-table tr.analyzed-row.selected:hover {
          background: #a7f3d0;
        }

        .checkbox-column {
          width: 40px;
          text-align: center;
        }

        .checkbox-column input[type="checkbox"] {
          width: 16px;
          height: 16px;
          accent-color: #3b82f6;
        }

        .email-cell {
          font-family: monospace;
          font-size: 0.85rem;
          color: #1f2937;
        }

        .website-cell a {
          color: #3b82f6;
          text-decoration: none;
          font-size: 0.85rem;
        }

        .website-cell a:hover {
          text-decoration: underline;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .status-badge.verified {
          background: #dcfce7;
          color: #166534;
        }

        .status-badge.analyzed {
          background: #dbeafe;
          color: #1e40af;
        }

        .status-badge.pending {
          background: #fef3c7;
          color: #92400e;
        }

        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 1rem;
          margin-top: 1.5rem;
          padding: 1rem;
        }

        .pagination-button {
          padding: 8px 16px;
          border: 1px solid #d1d5db;
          background: white;
          color: #374151;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .pagination-button:hover:not(:disabled) {
          background: #f3f4f6;
        }

        .pagination-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pagination-info {
          color: #6b7280;
          font-size: 0.9rem;
        }

        .loading-container, .error-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem;
          text-align: center;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f4f6;
          border-top: 4px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .error-icon {
          font-size: 2rem;
          color: #dc2626;
          margin-bottom: 1rem;
        }

        .retry-button {
          padding: 10px 20px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          margin-top: 1rem;
        }

        .retry-button:hover {
          background: #2563eb;
        }

        .no-results {
          text-align: center;
          padding: 3rem;
          color: #6b7280;
        }

        @media (max-width: 768px) {
          .verified-leads-table {
            padding: 1rem;
          }

          .table-controls {
            flex-direction: column;
            align-items: stretch;
          }

          .search-container {
            max-width: none;
          }

          .action-buttons {
            justify-content: center;
          }

          .leads-table {
            font-size: 0.8rem;
          }

          .leads-table th, .leads-table td {
            padding: 8px 12px;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default VerifiedLeadsTable;